/* "What's New" changelog — the data.
 *
 * THIS is the only file you edit to add an announcement: append an entry to the
 * `announcements` array below (between the @announcements markers). No components or logic
 * live here. The CI drafter (.github/workflows/draft-changelog.yaml → scripts/draftChangelog.ts)
 * also edits only this file, inserting new entries after the @announcements:start marker.
 *
 * Each `title` is self-contained localized data ({ en, es, ... }); English is the source of
 * truth and the fallback for any language without its own translation. Keep the @announcements
 * markers intact.
 */

export type AnnouncementKind = "feature" | "fix";

/** Language code -> text. `en` is required — the source of truth and the fallback. */
export type LocalizedText = { en: string } & Record<string, string>;

export type Announcement = {
  id: string; // stable, unique (e.g. "2026-07-mm-sex-split")
  date: string; // ISO YYYY-MM-DD — merge/release date, used for sorting and the "new since" badge
  kind: AnnouncementKind;
  area: string; // short category label, e.g. "Reports", "Territories", "Meetings"
  title: LocalizedText; // one-line, user-facing summary, per language
};

/** The changelog entries (any order — the app sorts newest-first). */
export const announcements: Announcement[] = [
  /* @announcements:start — new entries are inserted below this line by scripts/draftChangelog.ts (keep this marker) */
  {
    id: "2026-09-smpw-admins-can-now-edit-a-congregation-",
    date: "2026-09-30",
    kind: "feature",
    area: "SMPW",
    title: {
      "en": "SMPW admins can now override a congregation‑managed publisher’s name, email, and phone, with the original values shown and a reset option",
      "cs": "Správci SMPW nyní mohou přepsat jméno, e‑mail a telefon zvěstovatele spravovaného sborem, přičemž se zobrazí původní hodnoty a možnost obnovení",
      "de": "SMPW-Administratoren können jetzt Name, E‑Mail und Telefonnummer eines von der Versammlung verwalteten Verkündigers überschreiben, wobei die ursprünglichen Werte angezeigt werden und eine Option zum Zurücksetzen verfügbar ist",
      "el": "Οι διαχειριστές SMPW μπορούν τώρα να αντικαταστήσουν το όνομα, το e‑mail και το τηλέφωνο ενός ευαγγελιζομένου που διαχειρίζεται η εκκλησία, με εμφάνιση των αρχικών τιμών και επιλογή επαναφοράς",
      "es": "Los administradores de SMPW ahora pueden sobrescribir el nombre, el correo electrónico y el teléfono de un publicador gestionado por la congregación, con los valores originales visibles y una opción para restablecerlos",
      "fr": "Les administrateurs SMPW peuvent désormais remplacer le nom, l’e‑mail et le téléphone d’un proclamateur géré par l’assemblée, avec affichage des valeurs d’origine et une option de réinitialisation",
      "hu": "Az SMPW-adminisztrátorok mostantól felülírhatják a gyülekezet által kezelt hírnök nevét, e‑mail-címét és telefonszámát; az eredeti értékek láthatók, és visszaállításra is van lehetőség",
      "id": "Admin SMPW kini dapat menimpa nama, email, dan telepon penyiar yang dikelola sidang, dengan nilai asli ditampilkan dan opsi untuk mengatur ulang",
      "it": "Gli amministratori SMPW possono ora sovrascrivere nome, e‑mail e telefono di un proclamatore gestito dalla congregazione, con la visualizzazione dei valori originali e un'opzione di ripristino",
      "ja": "SMPW管理者は、会衆が管理する伝道者の名前、メールアドレス、電話番号を上書きできるようになりました。元の値が表示され、リセットすることもできます",
      "ko": "SMPW 관리자는 이제 회중이 관리하는 전도인의 이름, 이메일, 전화번호를 재정의할 수 있으며, 원래 값이 표시되고 재설정 옵션도 제공됩니다",
      "nl": "SMPW-beheerders kunnen nu de naam, het e‑mailadres en het telefoonnummer van een door de gemeente beheerde verkondiger overschrijven, waarbij de oorspronkelijke waarden worden getoond en een optie om te herstellen beschikbaar is",
      "pl": "Administratorzy SMPW mogą teraz nadpisać imię i nazwisko, e‑mail oraz telefon głosiciela zarządzanego przez zbór, z widocznymi pierwotnymi wartościami i opcją przywrócenia",
      "pt-BR": "Os administradores do SMPW agora podem substituir o nome, o e-mail e o telefone de um publicador gerenciado pela congregação, com os valores originais exibidos e uma opção para redefinir",
      "pt-PT": "Os administradores do SMPW podem agora substituir o nome, o e‑mail e o telefone de um publicador gerido pela congregação, com os valores originais apresentados e uma opção de reposição",
      "ro": "Administratorii SMPW pot acum să suprascrie numele, e‑mailul și telefonul unui vestitor gestionat de congregație, cu afișarea valorilor originale și o opțiune de resetare",
      "ru": "Администраторы SMPW теперь могут переопределять имя, e‑mail и телефон возвещателя, данные которого ведёт собрание, при этом отображаются исходные значения и есть возможность сброса",
      "th": "ผู้ดูแล SMPW สามารถแทนที่ชื่อ อีเมล และหมายเลขโทรศัพท์ของผู้ประกาศที่ประชาคมจัดการได้แล้ว โดยแสดงค่าเดิมและมีตัวเลือกให้รีเซ็ต",
      "tl": "Maaari nang palitan ng mga admin ng SMPW ang pangalan, email, at telepono ng mamamahayag na pinamamahalaan ng kongregasyon, habang ipinapakita ang orihinal na mga value at may opsiyong i-reset",
      "tr": "SMPW yöneticileri artık cemaat tarafından yönetilen bir müjdecinin adını, e‑postasını ve telefonunu geçersiz kılabilir; orijinal değerler gösterilir ve sıfırlama seçeneği sunulur",
      "uk": "Адміністратори SMPW тепер можуть перевизначати ім’я, e‑mail і телефон вісника, дані якого веде збір, при цьому показуються початкові значення та є можливість скидання",
      "vi": "Quản trị viên SMPW giờ đây có thể ghi đè tên, email và số điện thoại của công bố do hội thánh quản lý, với giá trị gốc được hiển thị và tùy chọn đặt lại",
      "zh": "SMPW 管理员现在可以覆盖由会众管理的传道员的姓名、电子邮件和电话，同时显示原始值并提供重置选项",
      "zh-Hant": "SMPW 管理員現在可以覆寫由會眾管理的傳道員的姓名、電子郵件和電話，同時顯示原始值並提供重設選項",
    },
  },
  {
    id: "2026-09-s-13-reports-are-available-as-a-docx-fil",
    date: "2026-09-28",
    kind: "feature",
    area: "Documents",
    title: {
      "en": "S‑13 reports are available as a DOCX file",
      "cs": "Zprávy S‑13 jsou k dispozici jako soubor DOCX",
      "de": "S‑13-Berichte sind als DOCX-Datei verfügbar",
      "el": "Οι αναφορές S‑13 είναι διαθέσιμες ως αρχείο DOCX",
      "es": "Los informes S‑13 están disponibles como archivo DOCX",
      "fr": "Les rapports S‑13 sont disponibles sous forme de fichier DOCX",
      "hu": "A S‑13 jelentések DOCX fájlként elérhetők",
      "id": "Laporan S‑13 tersedia sebagai file DOCX",
      "it": "I rapporti S‑13 sono disponibili come file DOCX",
      "ja": "S‑13レポートはDOCXファイルとして利用可能です",
      "ko": "S‑13 보고서는 DOCX 파일로 제공됩니다",
      "nl": "S‑13-rapporten zijn beschikbaar als DOCX-bestand",
      "pl": "Raporty S‑13 są dostępne jako plik DOCX",
      "pt-BR": "Os relatórios S‑13 estão disponíveis como arquivo DOCX",
      "pt-PT": "Os relatórios S‑13 estão disponíveis como ficheiro DOCX",
      "ro": "Rapoartele S‑13 sunt disponibile ca fișier DOCX",
      "ru": "Отчёты S‑13 доступны в формате DOCX",
      "sw": "Ripoti za S‑13 zinapatikana kama faili la DOCX",
      "th": "รายงาน S‑13 มีให้เป็นไฟล์ DOCX",
      "tl": "Ang mga ulat ng S‑13 ay magagamit bilang file na DOCX",
      "tr": "S‑13 raporları DOCX dosyası olarak mevcut",
      "uk": "Звіти S‑13 доступні у форматі DOCX",
      "vi": "Báo cáo S‑13 có sẵn dưới dạng tệp DOCX",
      "zh": "S‑13 报告以 DOCX 文件形式提供",
      "zh-Hant": "S‑13 報告以 DOCX 檔案形式提供",
    },
  }
  /* @announcements:end (keep this marker) */
];
